import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { ContextMenu } from '@/components/ui/ContextMenu'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ArrowLeft, GripVertical, RefreshCw, Shield, Trash2 } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import { canManageRoles } from '@/lib/roles'
import { rolesApi, type UserRoleRow, type AppRoleValue } from '@/lib/rolesApi'
import { useTranslation } from 'react-i18next'

const ROLE_LABEL: Record<AppRoleValue, string> = {
  execution: '수행',
  design: '설계',
  strategy: '전략 사업',
  super_admin: 'Super Admin',
}
const ROLE_RANK: Record<AppRoleValue, number> = { super_admin: 0, strategy: 1, design: 2, execution: 3 }
const ROLE_OPTION_LABEL: Record<AppRoleValue, string> = {
  super_admin: 'Super Admin — full access and role management',
  strategy: '전략 사업 — full project access, no platform administration',
  design: '설계 — read and download',
  execution: '수행 — read only',
}
const ROLE_COLUMNS: Array<{
  role: AppRoleValue
  description: string
  accent: string
  surface: string
}> = [
  {
    role: 'super_admin',
    description: 'Full access to all data, settings, users, and actions',
    accent: 'border-amber-400 dark:border-amber-600',
    surface: 'bg-amber-50/70 dark:bg-amber-950/20',
  },
  {
    role: 'strategy',
    description: 'Full project and engineering access; no accounts, licenses, audit logs, or platform administration',
    accent: 'border-blue-400 dark:border-blue-600',
    surface: 'bg-blue-50/70 dark:bg-blue-950/20',
  },
  {
    role: 'design',
    description: 'Read and download',
    accent: 'border-emerald-400 dark:border-emerald-600',
    surface: 'bg-emerald-50/70 dark:bg-emerald-950/20',
  },
  {
    role: 'execution',
    description: 'Read only',
    accent: 'border-slate-400 dark:border-slate-600',
    surface: 'bg-slate-50 dark:bg-slate-800/40',
  },
]

function rolePillClass(role: AppRoleValue): string {
  if (role === 'super_admin') return 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200'
  if (role === 'strategy') return 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200'
  if (role === 'design') return 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200'
  return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
}

export const RoleManagementPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { success, error } = useToast()
  const [rows, setRows] = useState<UserRoleRow[]>([])
  const [loading, setLoading] = useState(true)
  const [savingEmail, setSavingEmail] = useState<string | null>(null)
  const [draggedEmail, setDraggedEmail] = useState<string | null>(null)
  const [dropRole, setDropRole] = useState<AppRoleValue | null>(null)
  const [contextMenu, setContextMenu] = useState<{ row: UserRoleRow; x: number; y: number } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<UserRoleRow | null>(null)

  // viewing is internal (admin OR super); acting on the selectors is super-only,
  // mirroring the backend (GET /users = requireInternal, writes = requireSuperAdmin).
  const canView = canManageRoles(user)
  const canAct = canManageRoles(user)

  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await rolesApi.listUsers()
      setRows(data)
    } catch (err: any) {
      console.error('Error loading users:', err)
      error('Error', err.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [error])

  useEffect(() => {
    if (!user) return
    if (!canManageRoles(user)) {
      error('Access Denied', 'Super admin only')
      navigate('/dashboard')
      return
    }
    loadUsers()
  }, [user, navigate, error, loadUsers])

  const moveUser = async (row: UserRoleRow, target: AppRoleValue) => {
    if (target === row.role) return
    if (!canAct) return
    if (row.email === user?.email) {
      error('Role is locked', 'You cannot change your own role.')
      return
    }
    setSavingEmail(row.email)
    try {
      if (target !== 'execution' && !row.role_id) {
        await rolesApi.grant(row.email, target)          // POST — creates the row
      } else if (target !== 'execution' && row.role_id) {
        await rolesApi.change(row.role_id, target)        // PUT — admin ↔ super
      } else if (target === 'execution' && row.role_id) {
        await rolesApi.revoke(row.role_id)                // DELETE — back to user
      }
      success('Role updated', `${row.email} is now ${ROLE_LABEL[target]}`)
      await loadUsers()
    } catch (err: any) {
      // surface the server message verbatim (e.g. guardrail refusals)
      error('Could not change role', err.message || 'Failed to change role')
    } finally {
      setSavingEmail(null)
    }
  }

  const handleDrop = (event: React.DragEvent, target: AppRoleValue) => {
    event.preventDefault()
    setDropRole(null)
    const email = event.dataTransfer.getData('text/plain') || draggedEmail
    setDraggedEmail(null)
    if (!email) return
    const row = rows.find(item => item.email === email)
    if (row) void moveUser(row, target)
  }

  const deleteAccount = async (row: UserRoleRow) => {
    setSavingEmail(row.email)
    try {
      await rolesApi.deleteAccount(row.user_id)
      success('Account deleted', `${row.email} was permanently deleted`)
      await loadUsers()
    } catch (err: any) {
      error('Could not delete account', err.message || 'Failed to delete account')
    } finally {
      setSavingEmail(null)
    }
  }

  if (!user || !canView) return null

  const sorted = [...rows].sort((a, b) => {
    if (a.role !== b.role) return ROLE_RANK[a.role] - ROLE_RANK[b.role]
    return a.email.localeCompare(b.email)
  })
  const counts = rows.reduce(
    (acc, r) => { acc[r.role] = (acc[r.role] ?? 0) + 1; return acc },
    {} as Record<AppRoleValue, number>
  )

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Shield className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('dashboardPage.roleManagement')}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Drag accounts between RBAC roles. Right-click an account to delete it.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={loadUsers} disabled={loading} title={t('common.refresh')}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="mb-5 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
          {loading
            ? t('adminPages.loading')
            : `${rows.length} accounts · ${counts.super_admin ?? 0} Super Admin · ${counts.strategy ?? 0} 전략 사업 · ${counts.design ?? 0} 설계 · ${counts.execution ?? 0} 수행`}
          <span className="ml-1 text-slate-400">· {t('roleAdmin.unassignedHelp')}</span>
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {ROLE_COLUMNS.map(column => (
              <div key={column.role} className="h-72 animate-pulse rounded-xl bg-slate-200/70 dark:bg-slate-800" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white py-16 text-center text-slate-500 dark:border-slate-700 dark:bg-slate-800">
            {t('roleAdmin.empty')}
          </div>
        ) : (
          <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-4">
            {ROLE_COLUMNS.map(column => {
              const members = sorted.filter(row => row.role === column.role)
              const activeDrop = dropRole === column.role
              return (
                <section
                  key={column.role}
                  onDragOver={event => {
                    event.preventDefault()
                    event.dataTransfer.dropEffect = 'move'
                    setDropRole(column.role)
                  }}
                  onDragLeave={event => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropRole(null)
                  }}
                  onDrop={event => handleDrop(event, column.role)}
                  className={`min-h-[28rem] rounded-xl border-t-4 border-x border-b p-3 transition ${
                    column.accent
                  } ${column.surface} ${
                    activeDrop ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-900' : ''
                  }`}
                >
                  <div className="mb-3 flex items-start justify-between gap-3 px-1">
                    <div>
                      <h2 className="font-semibold text-slate-900 dark:text-white">{ROLE_LABEL[column.role]}</h2>
                      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{column.description}</p>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${rolePillClass(column.role)}`}>
                      {members.length}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {members.map(row => {
                      const movable = canAct && row.email !== user.email && savingEmail !== row.email
                      const isDragging = draggedEmail === row.email
                      return (
                        <article
                          key={row.email}
                          draggable={movable}
                          onDragStart={event => {
                            event.dataTransfer.effectAllowed = 'move'
                            event.dataTransfer.setData('text/plain', row.email)
                            setDraggedEmail(row.email)
                          }}
                          onDragEnd={() => {
                            setDraggedEmail(null)
                            setDropRole(null)
                          }}
                          onContextMenu={event => {
                            event.preventDefault()
                            setContextMenu({ row, x: event.clientX, y: event.clientY })
                          }}
                          className={`rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition dark:border-slate-700 dark:bg-slate-900 ${
                            movable ? 'cursor-grab active:cursor-grabbing hover:-translate-y-0.5 hover:shadow-md' : ''
                          } ${isDragging ? 'opacity-40' : ''} ${savingEmail === row.email ? 'animate-pulse opacity-60' : ''}`}
                        >
                          <div className="flex items-start gap-2">
                            <GripVertical className={`mt-0.5 h-4 w-4 shrink-0 ${movable ? 'text-slate-400' : 'text-slate-200 dark:text-slate-700'}`} />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-slate-900 dark:text-white" title={row.email}>
                                {row.email}
                              </div>
                              <div className="mt-1 text-[10px] text-slate-400">
                                {row.email === user.email
                                  ? 'You · locked'
                                  : row.updated_at
                                    ? `Changed ${new Date(row.updated_at).toLocaleDateString(i18n.resolvedLanguage)}`
                                    : 'Default role'}
                              </div>
                            </div>
                          </div>

                          {row.email !== user.email && (
                            <select
                              aria-label={`Move ${row.email} to role`}
                              value={row.role}
                              disabled={!canAct || savingEmail === row.email}
                              onChange={event => void moveUser(row, event.target.value as AppRoleValue)}
                              className="mt-3 w-full rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              <option value="super_admin">{ROLE_OPTION_LABEL.super_admin}</option>
                              <option value="strategy">{ROLE_OPTION_LABEL.strategy}</option>
                              <option value="design">{ROLE_OPTION_LABEL.design}</option>
                              <option value="execution">{ROLE_OPTION_LABEL.execution}</option>
                            </select>
                          )}
                        </article>
                      )
                    })}

                    {members.length === 0 && (
                      <div className="rounded-lg border border-dashed border-slate-300 px-3 py-10 text-center text-xs text-slate-400 dark:border-slate-700">
                        Drop users here
                      </div>
                    )}
                  </div>
                </section>
              )
            })}
          </div>
        )}
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          items={[{
            label: contextMenu.row.email === user.email ? 'Cannot delete your own account' : 'Delete account',
            icon: <Trash2 />,
            danger: true,
            disabled: contextMenu.row.email === user.email || savingEmail === contextMenu.row.email,
            onClick: () => setDeleteTarget(contextMenu.row),
          }]}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Delete account?"
        message={`Permanently delete ${deleteTarget?.email ?? 'this account'} and revoke all sessions? This cannot be undone.`}
        confirmText="Delete account"
        onConfirm={() => {
          if (deleteTarget) void deleteAccount(deleteTarget)
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
